// Tarjeta de la conversación del asistente: reglas puras de la fila "Resolver el reclamo"
// (botones que permite Mercado Libre ahora, confirmaciones y textos de resultado).
// Sin React: se prueban desde scripts/claim-resolve-view.test.ts.

/** Tipos de resolución, en el orden en que se muestran los botones. */
export const RESOLUTION_ORDER = [
  'allow_return',
  'claim_refund',
  'claim_partial_refund',
  'open_dispute',
] as const;
export type ResolutionKind = (typeof RESOLUTION_ORDER)[number];

export const RESOLUTION_LABEL: Record<string, string> = {
  allow_return: 'Aceptar devolución',
  claim_refund: 'Reembolso total',
  claim_partial_refund: 'Reembolso parcial',
  open_dispute: 'Escalar a Mercado Libre',
};

/** Color del botón (mismos que en Órdenes): ámbar, rojo, violeta, azul. */
export const RESOLUTION_TONE: Record<string, 'warning' | 'error' | 'violet' | 'info'> = {
  allow_return: 'warning',
  claim_refund: 'error',
  claim_partial_refund: 'violet',
  open_dispute: 'info',
};

export const CONFIRM_TEXT: Record<string, string> = {
  allow_return: '¿Seguro? El comprador va a despachar el producto de vuelta. No se puede deshacer.',
  claim_refund: '¿Seguro? Se devuelve TODO el dinero. No se puede deshacer.',
  claim_partial_refund:
    '¿Seguro? Se envía la oferta al comprador. Si la acepta, no se puede deshacer.',
  open_dispute: '¿Seguro? El reclamo pasa a un mediador de Mercado Libre. No se puede deshacer.',
};

export const NO_OPTIONS = 'Mercado Libre no ofrece acciones de resolución para este reclamo ahora.';
export const OPTIONS_FAIL = 'No se pudo consultar Mercado Libre. Probá en un momento.';
/** Texto fijo cuando la resolución falla sin texto del servidor (o se cayó la conexión). */
export const RESOLVE_FAIL = 'No se pudo resolver el reclamo. Probá en un momento.';
/** Texto fijo del panel de escalar. */
export const DISPUTE_NOTE = 'El reclamo pasa a un mediador de Mercado Libre. No se puede deshacer.';
export const REASON_MAX = 500;

/** El guardián de permisos del servidor responde `forbidden` (en inglés): se muestra esto. */
const NO_PERMISSION = 'No tenés permiso para esta acción.';

/** Opciones de resolución que devuelve el servidor para una tarjeta de reclamo. */
export interface ClaimOffer {
  percentage: number;
  amount: number | null;
  currency: string | null;
}

/** Costo de la devolución que devuelve el servidor (null = no se pudo conocer). */
export interface ReturnCost {
  amount: number;
  currency: string;
  amount_usd: number | null;
}

/**
 * Color de la línea del costo en la confirmación de "Aceptar devolución" (como en Órdenes):
 * con costo → ámbar; sin costo → verde; desconocido → gris (y se deja seguir igual).
 */
export function returnCostTone(rc: ReturnCost | null | undefined): 'cost' | 'free' | 'unknown' {
  if (!rc || !Number.isFinite(rc.amount)) return 'unknown';
  return rc.amount > 0 ? 'cost' : 'free';
}

export const RETURN_COST_CLASS: Record<'cost' | 'free' | 'unknown', string> = {
  cost: 'text-amber-700 dark:text-amber-400',
  free: 'text-green-600 dark:text-green-400',
  unknown: 'text-[var(--color-text-muted)]',
};

/** ¿La tarjeta es de un reclamo? Mensaje de reclamo, o factura enviada dentro de un reclamo. */
export function isClaimCard(a: { kind: string; params?: Record<string, unknown> | null }): boolean {
  if (a.kind === 'claim_send_message') return true;
  return a.kind === 'send_invoice' && a.params?.claim_id != null;
}

/** "50 % (≈ 15,30 USD)" o "50 %" si no hay monto. */
export function offerLabel(o: ClaimOffer): string {
  if (o.amount == null || !Number.isFinite(o.amount)) return `${o.percentage} %`;
  const n = o.amount.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${o.percentage} % (≈ ${n}${o.currency ? ` ${o.currency}` : ''})`;
}

/** Línea de resultado debajo de la fila. */
export function resolveOutcomeText(d: { executed: boolean; detail: string }): string {
  return d.executed ? `✓ ${d.detail}` : `✗ No se pudo: ${d.detail}`;
}

/**
 * Error del servidor al resolver: sus textos ya vienen en español simple (los técnicos los
 * reemplaza el propio servidor), así que pasan tal cual; vacío → texto fijo.
 */
export function resolveErrorText(error: string | null | undefined): string {
  const e = (error ?? '').trim();
  if (!e) return RESOLVE_FAIL;
  return e === 'forbidden' ? NO_PERMISSION : e;
}

/** Botones permitidos, en el orden fijo y sin tipos desconocidos. */
export function orderedAllowed(allowed: readonly string[]): ResolutionKind[] {
  return RESOLUTION_ORDER.filter((k) => allowed.includes(k));
}

/** Contador del motivo de escalar: "n / 500". */
export function reasonCounter(reason: string): string {
  return `${reason.length} / ${REASON_MAX}`;
}
